import * as THREE from 'three';

export type SlimeStyle = 'transparent' | 'cartoon';

export interface SlimeMesh {
  group: THREE.Group;
  body: THREE.Mesh;
  eyes: THREE.Group;
  innerBall: THREE.Mesh;
  baseHeight: number;
  baseRadius: number;
  bodyGeo: THREE.BufferGeometry;
  bodyBase: Float32Array;
  setStyle: (style: SlimeStyle) => void;
  /** Ganti warna badan (untuk custom pemain). */
  setColor: (hex: number) => void;
  /** Jari-jari permukaan badan TERDEFORMASI saat ini ke arah (dirX, dirZ)
   *  pada ketinggian lokal y. Untuk menaruh awal tali TEPAT di kulit
   *  (bukan di dalam badan yang transparan). */
  surfaceRadius: (dirX: number, dirZ: number, y: number) => number;
}

export const SLIME_COLOR = 0x5dffd4;
export const SLIME_EMISSIVE = 0x0e3b30;
const INNER_COLOR = 0x9fffe9;

export interface SlimeMaterial {
  material: THREE.MeshPhysicalMaterial;
  setStyle: (style: SlimeStyle) => void;
}

export function createSlimeMaterial(baseColor: number = SLIME_COLOR): SlimeMaterial {
  const material = new THREE.MeshPhysicalMaterial({
    color: baseColor,
    roughness: 0.12,
    metalness: 0,
    transmission: 0.35,
    thickness: 0.6,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
    emissive: SLIME_EMISSIVE,
    emissiveIntensity: 0.35,
    transparent: true,
    opacity: 0.92,
  });

  const setStyle = (style: SlimeStyle): void => {
    if (style === 'cartoon') {
      material.transmission = 0;
      material.transparent = false;
      material.opacity = 1;
      material.roughness = 0.5;
      material.emissiveIntensity = 0;
    } else {
      material.transmission = 0.35;
      material.transparent = true;
      material.opacity = 0.92;
      material.roughness = 0.12;
      material.emissiveIntensity = 0.35;
    }
    material.needsUpdate = true;
  };

  return { material, setStyle };
}

export function createSlime(radius = 0.55, height = 0.9, color: number = SLIME_COLOR): SlimeMesh {
  const group = new THREE.Group();
  group.name = 'Slime';

  const bodyGeo = new THREE.SphereGeometry(radius, 48, 40);
  bodyGeo.scale(1, height / (radius * 2), 1);
  bodyGeo.translate(0, height / 2, 0);

  const goo = createSlimeMaterial(color);
  const bodyMat = goo.material;
  // Badan selalu menulis depth + render SEBELUM trail (renderOrder 1 < 2):
  // jejak goo di bawah/napel badan ter-occlude — tidak tampak menembus
  // slime transparan, hanya terlihat di luar siluet badan.
  bodyMat.depthWrite = true;

  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.name = 'SlimeBody';
  body.renderOrder = 1;
  // Vertex dideformasi tiap frame TANPA update boundingSphere (mahal):
  // bounds stale + badan melebar/memanjang -> frustum-culling bisa
  // membuang badan sekejap di tepi layar (blink). Matikan saja
  // (satu mesh karakter, biaya nil).
  body.frustumCulled = false;
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const bodyBase = new Float32Array(bodyGeo.attributes.position.array as Float32Array);

  const innerGeo = new THREE.SphereGeometry(radius * 0.42, 24, 20);
  innerGeo.scale(1, (height * 0.42) / (radius * 0.42 * 2), 1);
  innerGeo.translate(0, height * 0.35, 0);
  const innerMat = new THREE.MeshStandardMaterial({
    color: INNER_COLOR,
    roughness: 0.5,
    emissive: INNER_COLOR,
    emissiveIntensity: 0.35,
    transparent: true,
    opacity: 0.55,
  });
  const innerBall = new THREE.Mesh(innerGeo, innerMat);
  innerBall.name = 'SlimeCore';
  innerBall.visible = false;
  group.add(innerBall);

  const eyeGroup = new THREE.Group();
  eyeGroup.name = 'SlimeEyes';
  const eyeGeo = new THREE.SphereGeometry(0.08, 16, 12);
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.1,
    emissive: 0xffffff,
    emissiveIntensity: 0.85,
  });
  const pupilGeo = new THREE.SphereGeometry(0.045, 16, 12);
  const pupilMat = new THREE.MeshStandardMaterial({ color: 0x0a0a14, roughness: 0.2 });

  const eyeY = height * 0.62;
  const eyeZ = radius * 0.82;

  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(radius * 0.32, eyeY, eyeZ);
  const pupilL = new THREE.Mesh(pupilGeo, pupilMat);
  pupilL.position.set(0, 0, 0.06);
  eyeL.add(pupilL);
  eyeGroup.add(eyeL);

  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(-radius * 0.32, eyeY, eyeZ);
  const pupilR = new THREE.Mesh(pupilGeo, pupilMat);
  pupilR.position.set(0, 0, 0.06);
  eyeR.add(pupilR);
  eyeGroup.add(eyeR);

  group.add(eyeGroup);

  const setStyle = (style: SlimeStyle): void => {
    goo.setStyle(style);
    innerBall.visible = false;
  };

  const setColor = (hex: number): void => {
    bodyMat.color.setHex(hex);
  };

  const surfaceRadius = (dirX: number, dirZ: number, y: number): number => {
    const dl = Math.hypot(dirX, dirZ);
    if (dl < 1e-4) return radius;
    const nx = dirX / dl;
    const nz = dirZ / dl;
    // Pindai vertex TERDEFORMASI (posisi atribut = hasil animator frame
    // berjalan): radius kulit ke arah normal pada pita ketinggian y.
    // O(2rb vertex) sekali per frame — murah, dan kebal squash/wobble.
    const arr = bodyGeo.attributes.position.array as Float32Array;
    let best = 0;
    for (let i = 0; i < arr.length; i += 3) {
      const vy = arr[i + 1];
      if (vy < y - 0.2 || vy > y + 0.2) continue;
      const d = arr[i] * nx + arr[i + 2] * nz;
      if (d > best) best = d;
    }
    if (best < 1e-4) return radius;
    return best + 0.01;
  };

  return {
    group,
    body,
    eyes: eyeGroup,
    innerBall,
    baseHeight: height,
    baseRadius: radius,
    bodyGeo,
    bodyBase,
    setStyle,
    setColor,
    surfaceRadius,
  };
}